import { useEffect } from "react";
import { formatPhone } from "@/lib/utils";
import { X, BadgeCheck, Globe, Briefcase, Award, Hash } from "lucide-react";
import { assetUrl } from "@/lib/api";
import type { BusinessListing } from "@/types";

// Brand gold accents (theme-following) for the advisor banner/avatar.
const NAVY  = "var(--color-primary)";
const NAVY2 = "var(--color-primary-glow)";

interface Props {
  listing: BusinessListing;
  onClose: () => void;
}

export default function AdvisorProfileModal({ listing, onClose }: Props) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const initials = listing.agent_name
    ? listing.agent_name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,37,84,0.45)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close — pinned to the card so it stays visible while the body scrolls */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 rounded-full bg-white/20 p-1.5 text-white hover:bg-white/30 transition"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
        {/* Header band */}
        <div className="h-20" style={{ background: `linear-gradient(135deg, ${NAVY}, ${NAVY2})` }} />

        {/* Avatar — overlaps header */}
        <div className="px-6 -mt-10 flex items-end gap-4">
          {listing.agent_avatar_url ? (
            <img
              src={assetUrl(listing.agent_avatar_url)!}
              alt={listing.agent_name}
              className="h-20 w-20 rounded-full object-cover ring-4 ring-white shadow-md shrink-0"
            />
          ) : (
            <div
              className="h-20 w-20 rounded-full ring-4 ring-white shadow-md shrink-0 flex items-center justify-center text-primary-foreground text-xl font-bold"
              style={{ background: `linear-gradient(135deg, ${NAVY2}, ${NAVY})` }}
            >
              {initials}
            </div>
          )}
          <div className="mb-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h2 className="text-base font-bold text-gray-900 leading-tight">{listing.agent_name}</h2>
              {listing.agent_verified && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                  <BadgeCheck className="h-3 w-3" /> Verified
                </span>
              )}
            </div>
            {listing.agent_agency_name && (
              <p className="text-xs text-gray-500 mt-0.5">{listing.agent_agency_name}</p>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-4 space-y-4">
          {/* Bio */}
          {listing.agent_bio && (
            <p className="text-sm text-gray-600 leading-relaxed italic border-l-2 pl-3" style={{ borderColor: NAVY2 }}>
              {listing.agent_bio}
            </p>
          )}

          {/* Details grid */}
          {(listing.agent_years_of_experience || listing.agent_license_number || listing.agent_website_url || listing.agent_email || listing.agent_phone) && (
            <div className="rounded-xl bg-gray-50 divide-y divide-gray-100">
              {listing.agent_years_of_experience != null && (
                <Row icon={<Briefcase className="h-3.5 w-3.5" />} label="Experience" value={`${listing.agent_years_of_experience} yrs`} />
              )}
              {listing.agent_license_number && (
                <Row icon={<Hash className="h-3.5 w-3.5" />} label="License #" value={listing.agent_license_number} />
              )}
              {listing.agent_email && (
                <Row icon={<Award className="h-3.5 w-3.5" />} label="Email" value={listing.agent_email} href={`mailto:${listing.agent_email}`} />
              )}
              {listing.agent_phone && (
                <Row icon={<Award className="h-3.5 w-3.5" />} label="Phone" value={formatPhone(listing.agent_phone)} href={`tel:${listing.agent_phone}`} />
              )}
              {listing.agent_website_url && (
                <Row
                  icon={<Globe className="h-3.5 w-3.5" />}
                  label="Website"
                  value={listing.agent_website_url.replace(/^https?:\/\//, "")}
                  href={listing.agent_website_url}
                  external
                />
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  icon, label, value, href, external,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
  external?: boolean;
}) {
  const content = href ? (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="text-primary hover:underline truncate"
    >
      {value}
    </a>
  ) : (
    <span className="text-gray-800 truncate">{value}</span>
  );

  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span className="text-gray-400 shrink-0">{icon}</span>
      <span className="text-xs text-gray-500 w-20 shrink-0">{label}</span>
      <span className="text-xs font-medium min-w-0 flex-1">{content}</span>
    </div>
  );
}
